"use client";

import React from "react";
import Image from "next/image";

type MegaCard = {
  title: string;
  description: string;
};

type NewMegaCardSectionProps = {
  leftImage: string;
  imageAlt?: string;
  cards?: MegaCard[];
  containerClassName?: string;
  layoutType?: "odd" | "even";
};

const NewMegaCardSection: React.FC<NewMegaCardSectionProps> = ({
  leftImage,
  imageAlt = "Background",
  cards = [],
  containerClassName,
  layoutType = "even",
}) => {
  const maxCards = 8;
  const limitedCards = cards.slice(0, maxCards);
  const totalCards = limitedCards.length;

  const rows: MegaCard[][] = [];
  const startIndex = totalCards === 7 ? 1 : 0;

  for (let i = startIndex; i < limitedCards.length; i += 2) {
    rows.push(limitedCards.slice(i, i + 2));
  }

  return (
    <section className={`relative w-full ${containerClassName || ""}`}>

      {/* ✅ Background Image Wrapper (2xl only) */}
      <div className="hidden 2xl:block absolute inset-0 -z-10">
        <Image
          src={leftImage}
          alt={imageAlt}
          fill
          priority
          className="object-cover"
        />
      </div>

      <div className="w-full  relative z-10">
        <div className="flex flex-col 2xl:flex-row items-stretch h-full container mx-auto">

          {/* Left panel spacer */}
          <div className="w-0 2xl:w-[35%]" />

          {/* Right Content */}
          <div className="w-full container mx-auto 2xl:w-[65%] h-full flex relative ">

            {/* Diagonal overlay (2xl only) */}
            <div
              className="hidden 2xl:block absolute top-0 left-0 w-full h-full bg-white/64 backdrop-blur-2xl"
              style={{
                clipPath: "polygon(16.666% 0%, 100% 0%, 100% 100%, 0% 100%)",
                zIndex: 0,
              }}
            />

            <div className="w-full relative z-10">

              {/* Sub-2xl Layout */}
              <div className=" 2xl:hidden w-full px-4 xl:px-0">
                {totalCards === 7 ? (
                  <>
                    {/* ===== ROW 1 ===== */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 md:gap-3 xl:border border-gray-200 xl:py-3 bg-black/2 xl:skew-x-6">
                      {limitedCards.slice(0, 4).map((card, index) => (
                        <div
                          key={index}
                          className="p-3 xl:p-6  border border-[#ccc]/48 section-bg-white-soft xl:-skew-x-12 hover:-translate-y-2 transition-all duration-300"
                        >
                          <div className="xl:skew-x-6">
                            <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                              {card.title}
                            </h3>
                            <p className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                              {card.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* ===== ROW 2 ===== */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 md:gap-4 xl:border border-gray-200 xl:py-3 bg-black/2 xl:skew-x-6 md:mt-4">
                      {limitedCards.slice(4, 7).map((card, index, arr) => {
                        const isLast = index === arr.length - 1; // ✅ correct

                        return (
                          <div
                            key={index}
                            className={`
          p-6 lg:p-8 border border-[#ccc]/48 section-bg-white-soft 
          xl:-skew-x-12 hover:-translate-y-2 transition-all duration-300
          ${isLast ? "col-span-2 lg:col-span-1" : ""}
        `}
                          >
                            <div className="xl:skew-x-6">
                              <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                                {card.title}
                              </h3>
                              <p className="mt-2 text-[#1b11b] text-sm md:text-base leading-relaxed">
                                {card.description}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>


                  </>
                ) : (
                  <>
                    <div className="hidden xl:block 2xl:hidden ">

                      {/* ===== ROW 1 ===== */}
                      <div
                        className={`
        grid grid-cols-1 sm:grid-cols-2
        md:gap-4
        border border-gray-100
        py-3
        bg-black/2
        xl:skew-x-6
        ${layoutType === "odd" ? "lg:grid-cols-3" : "lg:grid-cols-4"}
      `}
                      >
                        {limitedCards.slice(0, Math.ceil(limitedCards.length / 2)).map((card, index) => (
                          <div
                            key={index}
                            className="p-6 lg:p-8 border border-[#ccc]/48 section-bg-white-soft xl:-skew-x-12 hover:-translate-y-2 transition-all duration-300"
                          >
                            <div className="xl:skew-x-6">
                              <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                                {card.title}
                              </h3>
                              <p className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                                {card.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* ===== ROW 2 ===== */}
                      <div
                        className={`
        grid grid-cols-1 sm:grid-cols-2
        md:gap-4
        border border-gray-100
        mt-4 py-3
        bg-black/2
        xl:-skew-x-6
        ${layoutType === "odd" ? "lg:grid-cols-3" : "lg:grid-cols-4"}
      `}
                      >
                        {limitedCards.slice(Math.ceil(limitedCards.length / 2)).map((card, index) => (
                          <div
                            key={index}
                            className="p-6 lg:p-8 border border-[#ccc]/48 section-bg-white-soft xl:skew-x-12 hover:-translate-y-2 transition-all duration-300"
                          >
                            <div className="xl:-skew-x-6">
                              <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                                {card.title}
                              </h3>
                              <p className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                                {card.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>


                    <div className=" xl:hidden">
                      <div className="grid grid-cols-2">
                        {limitedCards.map((card, index) => (
                          <div
                            key={index}
                            className="p-6 lg:p-8 border border-[#ccc]/48 section-bg-white-soft xl:skew-x-12 hover:-translate-y-2 transition-all duration-300"
                          >
                            <div className="xl:-skew-x-6">
                              <h3 className="text-[#272d57] text-lg md:text-xl font-semibold">
                                {card.title}
                              </h3>
                              <p className="mt-2 text-[#1b1b1b] text-sm md:text-base leading-relaxed">
                                {card.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                  </>

                )}
              </div>


              {/* 2xl Layout - Two Columns */}
              <div className="hidden 2xl:block w-full pl-44">

                {/* ===== Special Case: 7 Cards ===== */}
                {totalCards === 7 && limitedCards[0] && (
                  <div className="grid grid-cols-1 border-b border-[#ccc]/48">
                    <div className="p-8">
                      <h3 className="text-[#272d57] text-xl font-semibold">
                        {limitedCards[0].title}
                      </h3>
                      <p className="mt-2 text-[#1b1b1b] leading-relaxed">
                        {limitedCards[0].description}
                      </p>
                    </div>
                  </div>
                )}

                {/* ===== Remaining Cards (Always 2 Columns) ===== */}
                <div className="grid grid-cols-2">
                  {(totalCards === 7 ? limitedCards.slice(1) : limitedCards).map(
                    (card, index) => (
                      <div
                        key={index}
                        className="p-8 border border-[#ccc]/48"
                      >
                        <h3 className="text-[#272d57] text-xl font-semibold">
                          {card.title}
                        </h3>
                        <p className="mt-2 text-[#1b1b1b] leading-relaxed">
                          {card.description}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewMegaCardSection;
